import type { TFunction } from "i18next";
import type { DateTime } from "luxon";
import type { CategoricalColor } from "../../../../utils/categoricalColors";
import { type NurseInShift, NurseInShiftCreationSource } from "../../../../utils/models";
import { type Holiday, type HolidayGroup, HolidayStatus } from "../holidayApi";
import { holidayGroupById, holidayGroupColor, holidayGroupLabel } from "../holidayFormat";

/** A group as the schedule shows it: "Group A" in its colour. */
export type ShownHolidayGroup = HolidayGroup & { label: string; color: CategoricalColor };

export type ScheduleHoliday = {
	holiday: Holiday;
	/** Applied or changed since: the applied group's shifts are on the schedule. Until then it is drawn dashed. */
	applied: boolean;
	/** The applied group once applied, else the chosen one. Null when no group is chosen, or the applied one was removed. */
	group: ShownHolidayGroup | null;
	/** The group chosen now. Differs from `group` only when the status is `changed`. */
	selectedGroup: ShownHolidayGroup | null;
};

const shownGroup = (t: TFunction, groups: readonly HolidayGroup[], groupId: string | null): ShownHolidayGroup | null => {
	if (groupId === null) return null;
	const group = holidayGroupById(groups, groupId);
	// DEFAULT: a group removed since the holidays loaded shows as no group until both refresh.
	if (group === undefined) return null;
	return { ...group, label: holidayGroupLabel(t, group), color: holidayGroupColor(groups, group.id) };
};

export const toScheduleHolidays = (t: TFunction, holidays: readonly Holiday[], groups: readonly HolidayGroup[]): ScheduleHoliday[] =>
	holidays.map((holiday) => {
		const applied = holiday.status !== HolidayStatus.NotApplied;
		const selectedGroup = shownGroup(t, groups, holiday.selectedGroupId);
		return { holiday, applied, group: applied ? shownGroup(t, groups, holiday.appliedGroupId) : selectedGroup, selectedGroup };
	});

/**
 * The applied holiday a shift was put on the schedule for: a Holiday shift that starts inside an applied holiday's
 * hours for its job. Undefined for every other shift, including one without a job (Apply always gives the person's job).
 */
export const holidayForShift = (
	shift: Pick<NurseInShift, "source" | "startTime" | "jobId">,
	holidays: readonly ScheduleHoliday[],
): ScheduleHoliday | undefined => {
	if (shift.source !== NurseInShiftCreationSource.Holiday) return undefined;
	const startMillis = shift.startTime.toMillis();
	return holidays.find(
		({ applied, holiday }) =>
			applied &&
			holiday.windows.some((window) => window.jobId === shift.jobId && window.start.toMillis() <= startMillis && startMillis < window.end.toMillis()),
	);
};

export type ScheduleHolidays = {
	holidays: readonly ScheduleHoliday[];
	/** The holiday dated on `date`'s calendar day, if any. */
	holidayOn: (date: DateTime) => ScheduleHoliday | undefined;
	/** Nothing could be loaded. A failed background reload keeps the holidays already shown, so it doesn't count. */
	isError: boolean;
};

/** The schedule's first and last loaded day; null until a week has loaded. */
export type ScheduleRange = { from: DateTime; to: DateTime } | null;
